import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Award, Shield, Wrench } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { iconMap } from "@/lib/serviceMeta";
import { sanitizeHtml } from "@/lib/sanitize";

const FALLBACK_VALUES = [
  { icon: Award, title: "13 שנות ניסיון", desc: "מומחיות שנבנתה פרויקט אחרי פרויקט" },
  { icon: Shield, title: "אחריות מלאה", desc: "עומדים מאחורי כל עבודה שאנחנו מבצעים" },
  { icon: Wrench, title: "פתרון 99%", desc: "כמעט כל בעיית גג – יש לנו את הפתרון" },
];

const AboutSection = () => {
  const { data: posts = [] } = useQuery({
    queryKey: ["posts", "about"],
    queryFn: async () => {
      const { data } = await supabase
        .from("posts")
        .select("*")
        .eq("section", "about")
        .order("sort_order");
      return data || [];
    },
  });

  const ref = useScrollReveal([posts]);

  const story = posts.find((p) => {
    const m = p.metadata as { type?: string } | null;
    return m?.type === "story";
  });
  const values = posts.filter((p) => {
    const m = p.metadata as { type?: string } | null;
    return m?.type === "value";
  }).slice(0, 3);

  return (
    <section id="about" className="py-20 bg-background" ref={ref}>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto animate-on-scroll">
          <div className="bg-card rounded-2xl shadow-lg p-8 md:p-12 border border-border">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6 text-center">
              הכירו את <span className="text-primary">אור</span>
            </h2>
            {story?.content_html ? (
              <div
                className="prose prose-sm md:prose-base max-w-3xl mx-auto text-muted-foreground text-center mb-8 [&_strong]:text-foreground"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(story.content_html) }}
              />
            ) : (
              <p className="text-lg text-muted-foreground leading-relaxed mb-8 text-center max-w-3xl mx-auto">
                שמי אור, ואני חי ונושם גגות כבר 13 שנה.
              </p>
            )}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {(values.length > 0
                ? values.map((v) => {
                    const m = v.metadata as { icon?: string } | null;
                    const Icon = (m?.icon && iconMap[m.icon]) || Award;
                    return { icon: Icon, title: v.title, desc: v.subtitle || "" };
                  })
                : FALLBACK_VALUES
              ).map((item, i) => (
                <div key={i} className="flex flex-col items-center text-center p-4">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                    <item.icon className="text-primary" size={28} />
                  </div>
                  <h3 className="font-bold text-foreground mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
